import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * BeaconTrophy — small badge strip rendered on a seller profile. Lists
 * every Beacon they've ever won (category × week). Each trophy is a
 * pill: amber for composite, slate for category-specific. Forever
 * proof that they earned the spotlight.
 *
 * The row is invisible if the seller has never won. Hover reveals
 * which week the trophy was earned.
 */
export default function BeaconTrophy({ userId }) {
  const [trophies, setTrophies] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    api
      .get(`/beacon/trophies/${userId}`)
      .then((r) => {
        if (!cancelled) {
          setTrophies(r.data?.items || []);
          setLoaded(true);
        }
      })
      .catch(() => setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (!loaded || !trophies.length) return null;

  return (
    <div
      className="mt-3 flex flex-wrap gap-1.5 items-center"
      data-testid="beacon-trophy-row"
    >
      <span className="text-[10px] uppercase tracking-widest font-bold text-amber-700 mr-1">
        Beacon Wins ({trophies.length})
      </span>
      {trophies.slice(0, 6).map((t) => {
        const isComposite = t.category === "composite";
        return (
          <span
            key={t.id}
            title={`${t.category_label} · ${t.week_iso}`}
            data-testid={`beacon-trophy-${t.category}`}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold border ${
              isComposite
                ? "bg-amber-300 text-amber-950 border-amber-400"
                : "bg-zinc-100 text-zinc-700 border-zinc-200"
            }`}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
              <path d="M5 3h14v3a5 5 0 0 1-3 4.58V13a4 4 0 0 1-3 3.87V19h3v2H8v-2h3v-2.13A4 4 0 0 1 8 13v-2.42A5 5 0 0 1 5 6V3zm2 2v1a3 3 0 0 0 3 3V5H7zm10 0h-3v4a3 3 0 0 0 3-3V5z" />
            </svg>
            {t.category_label}
          </span>
        );
      })}
      {trophies.length > 6 && (
        <span className="text-[10px] text-zinc-500">+{trophies.length - 6} more</span>
      )}
      <Link
        to={`/beacon-card/${userId}`}
        data-testid="beacon-trophy-share-cta"
        className="ml-1 inline-flex items-center gap-1 rounded-full bg-zinc-900 text-amber-200 hover:bg-zinc-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
      >
        Share →
      </Link>
    </div>
  );
}
